/**
 * Guidance Conversation booking API — pay-last flow: the customer picks their time and fills in
 * their details first (reserve), then pays as the very last step (charged immediately).
 * Kept separate from servicesService.js/bookingService so the existing
 * real-time BookConsultation flow is never touched.
 */

import { API_ENDPOINTS, BUSINESS_ID } from '../config/api';

export async function getGuidanceService() {
  const res = await fetch(API_ENDPOINTS.services.public({ category: 'guidance_call', businessId: BUSINESS_ID }));
  if (!res.ok) throw new Error('Failed to load guidance conversation service');
  const data = await res.json();
  const service = (data.services || [])[0];
  if (!service) throw new Error('Guidance conversation service is not available right now');
  return service;
}

/**
 * Saves the chosen time + contact details as an unpaid booking and returns the PaymentIntent
 * client secret for the final payment step. Pass `previousBookingId` if the customer went back
 * from the payment step, so the earlier unpaid reservation is retired.
 * Pass `authHeaders` (from useCustomerAuth().getAuthHeaders()) when the customer is logged in so
 * the booking links to their real account instead of just matching by typed email.
 */
export async function reserveGuidanceBooking({
  serviceId,
  amount,
  selectedIso,
  preferredTimeWindows,
  customerName,
  customerEmail,
  customerPhone,
  deliveryMethod,
  notes,
  previousBookingId,
  _hp,
  authHeaders = {},
}) {
  const res = await fetch(API_ENDPOINTS.guidanceBookings.reserve, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      serviceId,
      amount: amount || undefined,
      selectedIso,
      preferredTimeWindows,
      customerName,
      customerEmail,
      customerPhone: customerPhone || undefined,
      deliveryMethod: deliveryMethod || 'phone',
      notes: notes || undefined,
      previousBookingId: previousBookingId || undefined,
      _hp: _hp || undefined,
    }),
  });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || data.message || 'Could not reserve your time');
  return data;
}

/** Called right after the card is charged — verifies the payment and confirms the booking. */
export async function confirmGuidanceBookingPayment({ bookingId, stripePaymentIntentId }) {
  const res = await fetch(API_ENDPOINTS.guidanceBookings.confirmPayment(bookingId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stripePaymentIntentId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || 'Could not confirm your booking');
  return data;
}

/**
 * Logs demand for a day/window that doesn't currently fit the published availability —
 * no payment, requires a logged-in customer (authHeaders) so we can notify them later.
 */
export async function requestGuidanceTime({ serviceId, timezone, preferredDays, windowStart, windowEnd, notes, authHeaders = {} }) {
  const res = await fetch(API_ENDPOINTS.guidanceBookings.requestTime, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ serviceId, timezone, preferredDays, windowStart, windowEnd, notes: notes || undefined }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || 'Could not submit your request');
  return data;
}
